import { ResponsiveButton } from 'ui/ResponsiveButton/ResponsiveButton';
import React, { useContext, useEffect, useState } from 'react';
import { useLocation, Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { RQAPICallWithData } from 'store/APIClient';
import { useSelector } from 'store/hooks';
import RemoveIcon from '@mui/icons-material/Remove';
import GetAppIcon from '@mui/icons-material/GetApp';
import TitleIcon from '@mui/icons-material/Title';
import AddLinkIcon from '@mui/icons-material/AddLink';
import InsertPageBreakIcon from '@mui/icons-material/InsertPageBreak';
import ViewHeadlineIcon from '@mui/icons-material/ViewHeadline';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import Loading from 'components/Loading';
import OpenWithIcon from '@mui/icons-material/OpenWith';
import { trackEventBudget, TRACKABLE_EVENTS } from 'helpers/trackerHelpers';
import { RGLLayoutEngine } from 'lib/layout/engine/RGLLayoutEngine';
// TODO: make widget and layout types widget and layout specific and not dashboard specific
import PrintableGrid from 'routes/Dashboard/components/WidgetsLayout/PrintableGrid/PrintableGrid';
import PrintPaginationSeparators from 'routes/Dashboard/components/WidgetsLayout/PrintPaginationSeparators/PrintPaginationSeparators';
import PdfPreviewModal from 'components/PdfPreviewModal/PdfPreviewModal';
import { ArgumentT } from 'routes/BudgetModule/config/types';
import CurrentBudgetProposalProvider from 'routes/BudgetModule/components/BudgetProposal/Resource/contexts/CurrentBudgetProposalProvider';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import { ButtonMenu } from 'components/ButtonMenu';
import EditionLayoutBanner from 'components/LayoutHeader/EditionLayoutBanner';
import { WidgetT } from 'routes/Dashboard/components/WidgetsLayout/helpers';
import WidgetTruncatedWarningContext from 'components/Contexts/WidgetTruncatedWarningContext';
import {
    getPageSeparatorsAndWidgetsOverflow,
    isWidgetTruncated,
} from 'helpers/layoutHelpers';
import { useGetProjects } from 'routes/BudgetModule/shared/useGetProjects';
import ArgumentWidget from './ArgumentWidget';
import { PdfPreviewText } from './PdfPreviewText';
import { CurrentBudgetProposalContext } from 'routes/BudgetModule/components/BudgetProposal/Resource/contexts/CurrentBudgetProposalContext';
import { useArgumentLayout } from 'routes/BudgetModule/components/BudgetProposal/Resource/Arguments/Argument/hooks/useArgumentLayout';
import useScrollToWidgetFromHash from 'hooks/useScrollToWidgetFromHash';

const StyLink = styled(Link, {
    name: 'StyLink',
})(() => ({
    display: 'flex',
    alignItems: 'center',
    color: 'inherit',
    textDecoration: 'none',
}));

const StyBannerWrapper = styled(Box, {
    name: 'StyBannerWrapper',
})({
    position: 'sticky',
    top: '88px',
    zIndex: 1,
    padding: '0 1.5rem',
    marginBottom: '0.5rem',
    background: 'white',
});

const StyWrapperButton = styled(Box, {
    name: 'StyWrapperButton',
})({
    display: 'flex',
    flexFlow: 'row nowrap',
    gap: '1rem',
});

const pagePadding = '1.5rem';

const StyArgumentHeader = styled(Box, {
    name: 'StyArgumentHeader',
})({
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: pagePadding,
    /* align the argument title and actions button with the react-grid-layout card
     * left-padding is shorter because the icon has an inner blank space that is rather
     * painful to fix (viewBox) */
    paddingLeft: pagePadding,
    paddingRight: `calc(${pagePadding} + 10px)`,
    paddingBottom: pagePadding,
    position: 'sticky',
    top: 0,
    zIndex: 1,
    background: 'white',
});

const StyHeaderWrapper = styled(Box, {
    name: 'StyHeaderWrapper',
})({
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    justifyContent: 'flex-start',
});

const fetchArgument = async (argId: string) => {
    const response = await RQAPICallWithData<ArgumentT>({
        endpoint: `budget_proposals/arguments/${argId}`,
        method: 'GET',
    });
    return response.content.data;
};

/** Argument component, with its layout.
 *
 * Its state is organized as follows:
 * - a `widgets` , updated when moving widgets in edit mode, and sent to the backend state
 * when saving the layout.
 */
export function Argument(): React.ReactElement {
    const { argumentId = '' } = useParams();
    const { t } = useTranslation();
    const location = useLocation();
    const pathElements = location.pathname.split('/');
    const previousPath = pathElements
        .slice(0, pathElements.length - 1)
        .join('/');
    const navigate = useNavigate();

    const {
        isLoading: isArgumentLoading,
        data: argument,
        isError: isArgumentError,
    } = useQuery({
        queryKey: ['argument', argumentId],
        queryFn: () => fetchArgument(argumentId),
    });

    const layoutId = argument?.layoutId ?? null;

    const budgetProposal = useContext(CurrentBudgetProposalContext);
    const { isPdfModeActivated } = useSelector((state) => state.vision);

    const {
        widgets,
        setWidgets,
        addWidgetMutation,
        moveWidgetsMutation,
        loading: isLayoutLoading,
        error: isLayoutError,
    } = useArgumentLayout(layoutId);

    const userInitialYPosition = useScrollToWidgetFromHash(widgets);
    // Handle editMode and save the layout once triggered to false.
    const [editMode, setEditMode] = useState<boolean>(false);
    const toggleEditMode = () => {
        setEditMode(!editMode);
    };
    const [openPDFPreview, setOpenPDFPreview] = React.useState<boolean>(false);

    const menuItems = [
        {
            name: t('dashboardHeader.edit'),
            icon: <OpenWithIcon />,
            action: toggleEditMode,
        },
        {
            name: t('dashboardHeader.separatorElement'),
            icon: <RemoveIcon />,
            action: () => addWidgetMutation.mutate({ type: 'separator' }),
        },
        {
            name: t('widget.navlink.default'),
            icon: <AddLinkIcon />,
            action: () => addWidgetMutation.mutate({ type: 'navlink' }),
        },
        {
            name: t('dashboardHeader.titleElement'),
            icon: <TitleIcon />,
            action: () =>
                addWidgetMutation.mutate({
                    type: 'title',
                    content: { text: t('budgetProposal.defaultTitle') },
                }),
        },
        {
            name: t('dashboardHeader.paragraphElement'),
            icon: <ViewHeadlineIcon />,
            action: () =>
                addWidgetMutation.mutate({
                    type: 'paragraph',
                    content: { tiptap: '' },
                }),
        },
        {
            name: t('dashboardHeader.pageBreak'),
            icon: <InsertPageBreakIcon />,
            action: () =>
                addWidgetMutation.mutate({
                    type: 'pagebreak',
                }),
        },
    ];

    // TODO: do we really want to prefetch projects even if there isn't any widget
    // with inscriptions data?
    useGetProjects();

    const [shouldWarnWidgetTruncated, setShouldWarnWidgetTruncated] =
        useState<boolean>(false);

    // Deal with widget page overflow (widgets cut between 2 pages)
    const [widgetsWithPageOverflowRows, pageSeparators] =
        getPageSeparatorsAndWidgetsOverflow(widgets);

    useEffect(() => {
        setShouldWarnWidgetTruncated(
            widgetsWithPageOverflowRows.some(isWidgetTruncated),
        );
    }, [widgetsWithPageOverflowRows]);

    // NB the use of useCallback is required to avoid useless re-rendering when the
    // widgets contents are updated, for instance when the text of a paragraph is saved.
    const WidgetRender = React.useCallback(
        (widgetProps: { widget: WidgetT }) => (
            <ArgumentWidget
                {...widgetProps}
                editMode={editMode}
                setEditMode={setEditMode}
                lazyLoading={!isPdfModeActivated}
                isPdfModeActivated={isPdfModeActivated}
                layoutId={layoutId}
            />
        ),
        // NB we don't want to rerender all widgets each time one budget is updated
        // that is why we use a hook-maker and not a shared hook.
        [editMode, setEditMode, isPdfModeActivated, layoutId],
    );

    if (isArgumentLoading || isLayoutLoading) {
        return <Loading />;
    }
    if (
        isArgumentError ||
        isLayoutError ||
        moveWidgetsMutation.isError ||
        addWidgetMutation.isError ||
        !argument
    ) {
        return <Typography>{t('asyncComponent.error')}</Typography>;
    }

    const title = (
        <Typography variant="h4" color="primary">
            {argument.title}
        </Typography>
    );

    const trackingMetadata = {
        budgetProposal: budgetProposal.name,
        phase: t(`budgetProposal.${budgetProposal.phase}`),
        service: argument.resourceName,
        argument: argument.title,
    };

    const onBackButtonClicked = () => {
        trackEventBudget(
            TRACKABLE_EVENTS.budget.budgetProposal.argument.backButton,
            trackingMetadata,
        );
    };

    const argumentPdfUrl = `budget_proposals/argument/${argument.id}/pdf`;

    return (
        <CurrentBudgetProposalProvider
            budgetProposalId={argument.budgetProposalId}
        >
            <Box sx={{ marginBottom: '1.5rem' }}>
                <StyArgumentHeader>
                    <StyHeaderWrapper>
                        <StyLink
                            to={previousPath}
                            onClick={onBackButtonClicked}
                        >
                            <ChevronLeftIcon sx={{ fontSize: '32px' }} />
                            {title}
                        </StyLink>
                    </StyHeaderWrapper>
                    <StyWrapperButton>
                        <ButtonMenu
                            buttonLabel={
                                <Typography
                                    variant="body1SemiBold"
                                    color="primary"
                                    sx={{
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {t('dashboardHeader.updateLayoutElement')}
                                </Typography>
                            }
                            menuItems={menuItems}
                            extra={{
                                variant: 'outlined',
                                size: 'small',
                                endIcon: (
                                    <KeyboardArrowDownIcon fontSize="small" />
                                ),
                            }}
                        />
                        <ResponsiveButton
                            icon={<GetAppIcon color="primary" />}
                            label={t('dashboardHeader.pdfExport')}
                            extra={{
                                variant: 'outlined',
                                size: 'small',
                            }}
                            onClick={() => setOpenPDFPreview(true)}
                        />
                    </StyWrapperButton>
                </StyArgumentHeader>
                {openPDFPreview && (
                    <WidgetTruncatedWarningContext.Provider
                        value={{
                            shouldShowWidgetTruncatedWarning:
                                shouldWarnWidgetTruncated,
                        }}
                    >
                        <PdfPreviewModal
                            open={openPDFPreview}
                            url={argumentPdfUrl}
                            onClose={() => setOpenPDFPreview(false)}
                            title={t('budget.argumentWaitForPdfExport')}
                            hasDownloadButton
                            downloadTitle={argument.title || ''}
                            downloadTrackingEvent={{
                                name: TRACKABLE_EVENTS.budget.budgetProposal
                                    .argument.pdfExport,
                                payload: trackingMetadata,
                            }}
                            onSuccessText={<PdfPreviewText />}
                        />
                    </WidgetTruncatedWarningContext.Provider>
                )}
                {editMode ? (
                    <StyBannerWrapper>
                        <EditionLayoutBanner
                            toggleEditMode={toggleEditMode}
                            cancelDashboardEdition={() => {
                                // NOTE: ideally this refresh should not be necessary
                                // but it's difficult to make RGL return to the correct state
                                // without making it remount
                                navigate(0);
                            }}
                            saveDashboardEdition={() => {
                                moveWidgetsMutation.mutate();
                            }}
                        />
                    </StyBannerWrapper>
                ) : null}
                <Box sx={{ padding: '0 1.5rem' }}>
                    {widgets.length === 0 && (
                        <Typography>
                            {t('budgetProposal.emptyArgument')}
                        </Typography>
                    )}
                    {isPdfModeActivated && widgets.length > 0 && (
                        <PrintableGrid
                            widgets={widgetsWithPageOverflowRows}
                            WidgetRender={WidgetRender}
                            title={argument.title || ''}
                            filters={[]}
                            isDashboard={false}
                        />
                    )}
                    {editMode && widgets.length > 0 && (
                        <PrintPaginationSeparators
                            pageSeparators={pageSeparators}
                        />
                    )}
                    {!isPdfModeActivated && widgets.length > 0 && (
                        <RGLLayoutEngine
                            widgets={widgetsWithPageOverflowRows}
                            editMode={editMode}
                            Widget={WidgetRender}
                            onLayoutChange={setWidgets}
                            pageSeparators={pageSeparators}
                            saveWidgets
                            userInitialYPosition={userInitialYPosition}
                        />
                    )}
                </Box>
            </Box>
        </CurrentBudgetProposalProvider>
    );
}
